import React from 'react';
import { FormGroup, Input, InputGroup, InputGroupText } from 'reactstrap';
import ValidatedSelect, {
    ReactSelectStyles,
} from '../common/forms/ValidatedSelect';
import { FormLabel } from '../common/forms/FormElements';
import _ from 'lodash';
import cls from 'classnames';
import { v4 as uuid } from 'uuid';
import moment from 'moment';
import { MaxResourceCount } from '../longTermSchedule/LongTermSchedule';
import JobResourceOverrideList from './JobResourceOverrideList';
import ResourceApplicationUserGroupDisplayTable from './ResourceApplicationUserGroupDisplayTable';

export default class ResourceApplicationUserGroupMultiSelect
    extends React.Component
{
    extractApplicationUserGroupIdsFromJobResources = (managedJobResources) =>
        managedJobResources
            .filter(
                (mjr) =>
                    mjr.applicationUserGroupId &&
                    this.getJobResourceValue(mjr) > 0
            )
            .map((mjr) => mjr.applicationUserGroupId);

    constructor(props) {
        super(props);

        const managedJobResources = (props.jobResources || []).filter((jr) =>
            Boolean(jr.applicationUserGroupId)
        );

        let state = {
            isValid: true,
            originalJobResources: managedJobResources,
            managedJobResources,
            uuid: uuid(),
            entityId: props.entityId || null,
        };

        state.managedApplicationUserGroupIds = managedJobResources
            .filter((mjr) => this.getJobResourceValue(mjr) > 0)
            .map((mjr) => mjr.applicationUserGroupId);

        this.state = state;
    }

    onInternalJobResourceChange = (selectedJobResources) => {
        let {
            managedJobResources,
            managedApplicationUserGroupIds,
            originalJobResources,
        } = this.state;

        managedApplicationUserGroupIds = selectedJobResources.map(
            (jr) => jr.id
        );

        managedJobResources = managedJobResources.filter((mjr) =>
            selectedJobResources.some(
                (aug) => aug.id == mjr.applicationUserGroupId
            )
        );

        const newManagedApplicationUserGroups = selectedJobResources.filter(
            (aug) =>
                !managedJobResources.some(
                    (mjr) => mjr.applicationUserGroupId == aug.id
                )
        );

        if (
            newManagedApplicationUserGroups &&
            newManagedApplicationUserGroups.length
        ) {
            managedJobResources = managedJobResources.concat(
                originalJobResources.find((ojr) =>
                    newManagedApplicationUserGroups
                        .map((aug) => aug.id)
                        .includes(ojr.applicationUserGroupId)
                )
            );
        }

        const isValid = this.areEntriesValid(managedJobResources);

        this.setState(
            { isValid, managedJobResources, managedApplicationUserGroupIds },
            () => {
                this.signalUpdate();
            }
        );
    };

    areEntriesValid = (managedJobResources) => {
        const { dayName } = this.props;

        return managedJobResources.every((mjr) => mjr[dayName] > 0);
    };

    signalUpdate = () => {
        const { managedJobResources, isValid, managedApplicationUserGroupIds } =
            this.state;

        const { onManagedJobResourceCountChange, dayName } = this.props;

        onManagedJobResourceCountChange({
            managedJobResources: managedJobResources.filter((mjr) =>
                managedApplicationUserGroupIds.includes(
                    mjr.applicationUserGroupId
                )
            ),
            isValid,
            dayName,
        });
    };

    onInternalCountChange = (dataContainer, e) => {
        const { managedJobResources } = this.state;
        const { dayName } = this.props;

        const { applicationUserGroupId } = dataContainer;

        const index = _.findIndex(
            managedJobResources,
            (mjr) => mjr.applicationUserGroupId == applicationUserGroupId
        );

        const newCount = !isNaN(e.target.value) ? Number(e.target.value) : 0;

        managedJobResources[index][dayName] = newCount;

        const isValid = this.areEntriesValid(managedJobResources);

        this.setState({ managedJobResources, isValid }, () => {
            this.signalUpdate();
        });
    };

    getJobResourceValue = (jobResource) => {
        const { dayName } = this.props;

        if (!jobResource || !dayName) {
            return null;
        }

        return jobResource[dayName.toLowerCase()];
    };

    tooMuchDefined = (jobResource) => {
        const { dayName } = this.props;

        if (!jobResource || !dayName) {
            return null;
        }

        return jobResource[dayName.toLowerCase()] > MaxResourceCount;
    };

    onJobResourceStartTimesChanged = (jobResource, times) => {
        const { dayOfDate, signalChange } = this.props;

        const workDate = dayOfDate ? moment(dayOfDate) : moment();
        const normalizedOverrides = (times || [])
            .filter((timeEntry) => moment(timeEntry.value, 'HH:mm', true).isValid())
            .map((timeEntry) => {
                const parsedTime = moment(timeEntry.value, 'HH:mm', true);

                return {
                    id:
                        !timeEntry.id ||
                        timeEntry.id.constructor === String
                            ? null
                            : timeEntry.id,
                    date: workDate.clone().startOf('day').toDate(),
                    startTime: workDate
                        .clone()
                        .hour(parsedTime.hour())
                        .minute(parsedTime.minute())
                        .second(0)
                        .millisecond(0)
                        .toDate(),
                    arcGisOriginId: timeEntry.startLocationId ?? null,
                    arcGisDestinationId: timeEntry.endLocationId ?? null,
                    roadName:
                        timeEntry.startLocationId || timeEntry.endLocationId
                            ? ''
                            : timeEntry.startLocationText ?? '',
                };
            });

        jobResource.jobResourceOverrides = normalizedOverrides;

        if (signalChange) {
            signalChange();
        }
    };

    getOrderedApplicationUserGroups = () => {
        const { managedJobResources, managedApplicationUserGroupIds } =
            this.state;

        if (!managedJobResources) {
            return [];
        }

        const { applicationUserGroups } = this.props;

        const filteredJobResources = managedJobResources.filter((mjr) =>
            managedApplicationUserGroupIds.includes(mjr.applicationUserGroupId)
        );

        filteredJobResources.sort((a, b) => {
            const applicationUserGroupA = applicationUserGroups.find(
                (aug) => a.applicationUserGroupId == aug.id
            );
            const applicationUserGroupB = applicationUserGroups.find(
                (aug) => b.applicationUserGroupId == aug.id
            );

            if (applicationUserGroupA && !applicationUserGroupB) {
                return -1;
            }

            const groupNameA = (applicationUserGroupA || {}).groupName || null;
            const groupNameB = (applicationUserGroupB || {}).groupName || null;

            if (
                (!applicationUserGroupA && !applicationUserGroupB) ||
                groupNameA == groupNameB
            ) {
                return 0;
            }

            return groupNameA < groupNameB ? -1 : 1;
        });

        return filteredJobResources;
    };

    render() {
        const {
            applicationUserGroups,
            labelText,
            job,
            disabled,
            dayOfDate,
            arcGisLocations,
            jobStartTime,
        } = this.props;

        const { uuid, managedApplicationUserGroupIds } = this.state;

        const filteredJobResources = this.getOrderedApplicationUserGroups();
        const workDateLabel = dayOfDate
            ? moment(dayOfDate).format('MM/DD/YYYY')
            : '';

        return (
            <FormGroup className="resource-application-user-group-multi-select">
                {labelText && (
                    <FormLabel
                        htmlFor="entityApplicationUserGroups"
                        text={labelText}
                    />
                )}

                {!job.id && (
                    <ValidatedSelect
                        name="entityApplicationUserGroups"
                        options={applicationUserGroups}
                        getOptionValue={(aug) => aug.id}
                        getOptionLabel={(aug) => aug.groupName}
                        isMulti
                        value={
                            applicationUserGroups.filter((aug) =>
                                managedApplicationUserGroupIds.includes(aug.id)
                            ) ?? ''
                        }
                        onChange={this.onInternalJobResourceChange}
                        hideClear
                        menuPortalTarget={
                            typeof document !== 'undefined'
                                ? document.body
                                : null
                        }
                        menuPosition="fixed"
                        styles={{
                            ...ReactSelectStyles,
                            menuPortal: (base) => ({
                                ...base,
                                zIndex: 9999,
                            }),
                        }}
                        disabled={disabled}
                    />
                )}

                {!job.id &&
                    filteredJobResources.map((mjr, i) => {
                        const applicationUserGroup = applicationUserGroups.find(
                            (aug) => aug.id == mjr.applicationUserGroupId
                        );

                        if (!applicationUserGroup) {
                            return null;
                        }

                        const resourceCount = this.getJobResourceValue(mjr) || 0;

                        return (
                            <JobResourceOverrideList
                                key={`${uuid}-${i}`}
                                max={resourceCount}
                                arcGisLocations={arcGisLocations}
                                jobStartTime={jobStartTime ?? job?.startTime}
                                jobResourceOverrides={mjr.jobResourceOverrides}
                                onOverridesChange={this.onJobResourceStartTimesChanged.bind(
                                    this,
                                    mjr
                                )}
                                disabled={disabled}
                                resourceLabel={applicationUserGroup.groupName}
                                workDateLabel={workDateLabel}
                            >
                                {(addButton) => (
                                    <InputGroup className="mt-2">
                                        <InputGroupText>
                                            {applicationUserGroup.groupName}
                                        </InputGroupText>

                                        <Input
                                            value={this.getJobResourceValue(mjr)}
                                            className={cls({
                                                invalid:
                                                    !this.getJobResourceValue(
                                                        mjr
                                                    ) ||
                                                    this.tooMuchDefined(mjr),
                                            })}
                                            onChange={this.onInternalCountChange.bind(
                                                this,
                                                {
                                                    applicationUserGroupId:
                                                        mjr.applicationUserGroupId,
                                                }
                                            )}
                                            inputMode="numeric"
                                            invalid={
                                                !this.getJobResourceValue(mjr) ||
                                                this.tooMuchDefined(mjr)
                                            }
                                        />

                                        {addButton}
                                    </InputGroup>
                                )}
                            </JobResourceOverrideList>
                        );
                    })}

                {job.id && (
                    <ResourceApplicationUserGroupDisplayTable
                        {...this.props}
                        filteredJobResources={filteredJobResources}
                    />
                )}
            </FormGroup>
        );
    }
}

